/**
 * הזמנת משתמש — and the field that is deliberately absent. OA-0001 §3.7.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ONE ORDERED PROCESS, THREE SEPARATE ACTS. **Section 4, 2026-10-26.**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The Owner: *"a prominent 'add user' button is required, and an orderly process: the user's
 * details · choosing a role · choosing companies and an access level · summary and confirmation ·
 * showing the state of the invitation and what is needed to complete it."*
 *
 * THE CONTROL THIS SCREEN WAS BUILT TO ENFORCE, AND WHY THE WIZARD DOES NOT BREAK IT.
 *
 * OA-0001 §3.7: while an invitation CARRIED a role, a principal holding only `tenant.user.invite`
 * could mint an account holding ANY role in the tenant — including a copy of the bootstrap
 * administrator — and then sign in as it. That collapses *what a user may do* into *who may
 * invite*, which the document forbids in as many words: *"They do not collapse into one admin
 * flag."*
 *
 * What is forbidden is the SERVER accepting a role on the invitation route, and it still does not:
 * `POST /api/tenant/users/invitations` reads `displayName` and `email` and nothing else. This
 * screen now walks an administrator through THREE SEPARATE CALLS — invite, then
 * `tenant.user.role.assign`, then `tenant.grant.create` — each still gated on its own capability,
 * each still writing its own table. The convenience is in the ORDER, never in the authorisation.
 *
 * A session holding only `tenant.user.invite` is therefore shown step 1 and told, in the summary,
 * which of the other two it may not perform and who can.
 *
 * ── AND IT IS NOT ATOMIC, SO IT NEVER CLAIMS TO BE ──────────────────────────────────────────────
 *
 * Three calls can partly succeed: the invitation can be created and the role assignment refused.
 * The result names each act separately — invited / role assigned / access granted — because one
 * green message covering three writes is how somebody concludes that a person has access when
 * they have an account and nothing else.
 *
 * The role and the grant are attempted only if the invited person is already resolvable as a
 * membership. Where they are not, the screen SAYS the two remaining acts are still outstanding and
 * links to where they are done, rather than reporting a completion it did not achieve.
 *
 * ── WHAT ELSE IS ABSENT ─────────────────────────────────────────────────────────────────────────
 *
 *   · No password field, and no password anywhere in the response. 04 §2.2: *"No path may ever
 *     produce, transmit or display a password."* Gate B found Legacy's create-user hard-coding a
 *     default AND RETURNING IT IN THE JSON; the invitation is the shape that replaces it.
 *   · No company. A grant is `companies.assignment.manage`, on its own screen. D5: no role,
 *     ownership flag or seniority implies a grant.
 *   · No seat toggle. Seat capacity is checked by the server BEFORE the invitation is issued, and an
 *     unaccepted invitation consumes no seat until it is redeemed.
 *
 * ── THE GAP THAT WAS NAMED HERE IS NOW CLOSED ───────────────────────────────────────────────────
 *
 * A previous round recorded that the seat-limit refusal — `409 {error: 'limit_reached', limit,
 * current, upgradePath}` — could not reach this screen, because the client's taxonomy kept only a
 * support reference from a conflict body and widening a binding contract is not something one screen
 * may do on its own. `ConflictError` now carries the count, the limit and the upgrade path across, so
 * the refusal here is the same count/limit/route notice 08 §7 requires and that
 * `TenantCompaniesScreen` already showed — one treatment, on both screens, from one component.
 *
 * ── AND A 202 IS NOT A 200 ──────────────────────────────────────────────────────────────────────
 *
 * The invite route answers 202 with `delivery` when the mail port suppressed or refused the message.
 * The invitation EXISTS in that case — the row is written, the token is minted, the seat is consumed
 * — and only the message did not go out. Reporting that as "ההזמנה נשלחה" is how an office waits a
 * week for somebody who was never told, and then invites them again and pays for a second seat. The
 * three outcomes get three different states here, from `deliveryText.ts`.
 */

import { useCallback, useState } from 'react';
import type { TextKey } from '../i18n/catalogue';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { CapacityRefusalNotice } from '../components/CapacityPanel';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { callOperation } from '../api/operations';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import type { MailDeliveryReason } from '../api/contract';
import { MAIL_DELIVERY_REASONS } from '../api/contract';
import { deliveryTextFor } from '../api/deliveryText';
import { routes } from '../routes';
import { useServerData } from '../api/useServerData';
import { ServerRead } from '../components/ServerRead';
import { accessLevelHe, roleLabel } from '../api/adminVocabulary';
import { OFFERED_ACCESS_LEVELS } from '../api/contract';
import type { AccessLevel } from '../api/contract';

/**
 * WHAT BECAME OF ONE OF THE THREE ACTS.
 *
 * `not-asked` and `outstanding` are different facts and must not share a word: the first is *you
 * chose no role*, the second is *you chose one and it has not been applied yet*. Collapsing them
 * is how an administrator reads "no role" as a decision they made rather than as work left to do.
 */
type ActResult = 'done' | 'recorded' | 'refused' | 'outstanding' | 'not-asked';

/**
 * ONE CHOICE THE SERVER IS HOLDING FOR AN INVITATION THAT HAS NOT BEEN REDEEMED. **Section 7.**
 *
 * `companyName` is nullable because a company deleted between the choice and the arrival does not
 * erase the fact that the choice was made -- the redemption records that refusal by name.
 */
type PendingIntent = {
  readonly kind: string;
  readonly roleCode: string | null;
  readonly companyName: string | null;
  readonly accessLevel: string | null;
};

function actHe(t: (key: TextKey) => string, result: ActResult): string {
  switch (result) {
    case 'done':
      return t('act.done');
    case 'refused':
      return t('act.refused');
    case 'recorded':
      return t('act.recorded');
    case 'outstanding':
      return t('act.outstanding');
    default:
      return t('act.notAsked');
  }
}

type Outcome =
  | {
      readonly kind: 'invited';
      readonly invitationUid: string;
      /** `null` ⇒ the provider accepted the message. Otherwise the office has to be told which. */
      readonly delivery: MailDeliveryReason | null;
      readonly role: ActResult;
      readonly grant: ActResult;
      /**
       * WHAT THE SERVER SAYS IS WAITING, read back after the recording. **Section 7.**
       *
       * Not the form's own lists: the Owner's complaint was a wizard reporting choices it had not
       * persisted, and a screen that confirms its own input has no way to tell the two apart.
       * Empty is a legitimate answer and is printed as one.
       */
      readonly pending: readonly PendingIntent[];
    }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function isDeliveryReason(value: unknown): value is MailDeliveryReason {
  return typeof value === 'string' && (MAIL_DELIVERY_REASONS as readonly string[]).includes(value);
}

/**
 * THE FIELDS THIS FORM SENDS. Exported so a test can assert the list, because the assertion is
 * about what is ABSENT and an absence is not otherwise checkable.
 *
 * OA-0001 §3.7: no `roleCode`, no `capabilities`, no `companyUids`, no `password`, no `isAdmin`.
 */
export const INVITATION_FIELDS: readonly string[] = ['displayName', 'email'];

export function TenantUserInvitationScreen(): JSX.Element {
  const { t } = useLocale();

  const ctx = useWorkContext();

  /** What the administrator has chosen so far. Nothing is sent until the summary is confirmed. */
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [roleCodes, setRoleCodes] = useState<readonly string[]>([]);
  const [companyUids, setCompanyUids] = useState<readonly string[]>([]);
  const [level, setLevel] = useState<AccessLevel>('read');
  /*
   * THE TWO EXPLICIT CHOICES. **Section 4, 2026-10-27.**
   *
   * Owner: *"לא להפתיע בסיכום עם „ללא תפקיד” בלי שהמשתמש הבין ובחר בכך במפורש."*
   *
   * Leaving a step empty and DECIDING to leave it empty are different acts, and the summary used
   * to print the first as though it were the second. These two booleans are that decision. They
   * are mutually exclusive with the lists beside them -- ticking a role clears the opt-out and
   * ticking the opt-out clears the roles -- so there is never a state that means both.
   */
  const [noRole, setNoRole] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const readRoles = useCallback(() => callOperation('tenant.role.read', {}), []);
  const readRegister = useCallback(() => callOperation('companies.register.read', {}), []);
  const roles = useServerData(readRoles);
  const register = useServerData(readRegister);

  /*
   * WHICH STEPS THIS SESSION MAY PERFORM. The three acts keep their own capabilities (see the
   * header), so a session that cannot assign a role is not walked through choosing one and then
   * refused at the end -- the step is simply not offered, and the summary says who can do it.
   */
  const capabilities = ctx.session?.capabilities ?? [];
  const mayAssignRole = capabilities.includes('tenant.role.manage');
  const mayGrantAccess = capabilities.includes('companies.assignment.manage');

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('invite.title')} requires="TENANT" actual={ctx.surface} />;
  }

  /** The steps actually shown, in order. Numbers are positions in THIS list, never fixed. */
  const STEPS: readonly { readonly id: string; readonly labelHe: string }[] = [
    { id: 'details', labelHe: t('invite.stepDetails') },
    ...(mayAssignRole ? [{ id: 'role', labelHe: t('invite.stepRole') }] : []),
    ...(mayGrantAccess ? [{ id: 'access', labelHe: t('invite.stepAccess') }] : []),
    { id: 'summary', labelHe: t('invite.stepSummary') },
  ];
  /*
   * AND THE STEPS THAT ARE MISSING BECAUSE OF PERMISSIONS, NAMED. **Section 4, 2026-10-27.**
   *
   * Owner: *"להבחין בין שלב שלא מולא לבין שלב שאינו זמין בגלל הרשאות."* They were simply absent,
   * which reads as the first while being the second -- and an administrator who cannot assign a
   * role had no way to know whether they had missed a step or never had it.
   *
   * They stay OUT of the numbered list on purpose: numbering counts the steps this person fills,
   * so a session with three steps is 1-2-3. These are stated beside it, with the reason.
   */
  const unavailableSteps: readonly string[] = [
    ...(mayAssignRole ? [] : [t('invite.stepRole')]),
    ...(mayGrantAccess ? [] : [t('invite.stepAccess')]),
  ];
  const current = STEPS[step - 1];
  const detailsReady = displayName.trim() !== '' && email.trim() !== '';
  /*
   * WHETHER THE CURRENT STEP HAS BEEN ANSWERED. The continue button reads it, and so does the
   * sentence under the button -- a disabled control that does not say why is the thing this
   * product refuses elsewhere, and it was doing it here.
   */
  const roleDecided = roleCodes.length > 0 || noRole;
  const accessDecided = companyUids.length > 0 || noAccess;
  const blockedHe =
    current?.id === 'details' && !detailsReady
      ? t('invite.detailsNeeded')
      : current?.id === 'role' && !roleDecided
        ? t('invite.roleDecision')
        : current?.id === 'access' && !accessDecided
          ? t('invite.accessDecision')
          : null;

  /*
   * THE NAMES BEHIND THE CODES, for the summary. **Section 4:** *"בסיכום להציג שמות תפקידים
   * וחברות ורמת גישה, ולא רק מספרים."* The summary printed `תפקיד: 2`, which tells an
   * administrator nothing about what they are about to grant.
   *
   * Read from the SAME answers the choice lists were drawn from, so a name here cannot differ
   * from the name that was ticked.
   */
  const roleRows = roles.result.state === 'loaded' ? roles.result.data.roles : [];
  const companyRows = register.result.state === 'loaded' ? register.result.data.companies : [];
  const chosenRoleNames = roleRows
    .filter((role) => roleCodes.includes(role.roleCode))
    .map((role) => roleLabel(role));
  const chosenCompanyNames = companyRows
    .filter((company) => companyUids.includes(company.companyUid))
    .map((company) => company.displayName);

  /**
   * THE NAME BEHIND A ROLE CODE THE SERVER SENT BACK. **Section 7.**
   *
   * *"בסיכום להציג שמות תפקידים וחברות ורמת גישה, ולא רק מספרים."* The wire carries a stable code,
   * as every transport in this product does, and the Hebrew is applied from the SAME answer the
   * choice list was drawn from -- so a name printed here cannot differ from the name that was
   * ticked. A code with no matching row falls back to the CODE rather than to an empty cell: an
   * unrecognised role is a thing to report, not a thing to hide.
   */
  const roleNameFor = (code: string | null): string => {
    const row = roleRows.find((r) => r.roleCode === code);
    return row === undefined ? (code ?? '') : roleLabel(row);
  };

  /**
   * THE THREE ACTS, IN ORDER, EACH REPORTED SEPARATELY.
   *
   * They are three server calls and they can partly succeed, so this never collapses them into one
   * verdict. The role and the grant are attempted only once the invited person RESOLVES to a
   * membership -- until they do, `tenant.user.role.assign` and `tenant.grant.create` have no
   * `membershipUid` to name, and the result says the two acts are still outstanding rather than
   * reporting a completion that did not happen.
   */
  async function confirm(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      // A write. Never retried -- the client cannot know whether the server committed, and a
      // duplicated invitation is a second seat consumed against a limit.
      const created = await callOperation('tenant.user.invite', { body: { displayName, email } });

      let roleResult: ActResult = roleCodes.length === 0 ? 'not-asked' : 'outstanding';
      let grantResult: ActResult = companyUids.length === 0 ? 'not-asked' : 'outstanding';

      const members = await callOperation('tenant.member.read', {});
      const invited = members.members.find(
        (m) => m.email.trim().toLowerCase() === email.trim().toLowerCase(),
      );

      if (invited !== undefined) {
        if (roleCodes.length > 0 && mayAssignRole) {
          try {
            await callOperation('tenant.user.role.assign', {
              body: { membershipUid: invited.membershipUid, roleCodes },
            });
            roleResult = 'done';
          } catch {
            roleResult = 'refused';
          }
        }
        if (companyUids.length > 0 && mayGrantAccess) {
          try {
            await callOperation('tenant.grant.create', {
              body: { membershipUid: invited.membershipUid, companyUids, accessLevel: level },
            });
            grantResult = 'done';
          } catch {
            grantResult = 'refused';
          }
        }
      } else {
        /*
         * ══ THE CHOICES ARE RECORDED, BECAUSE THERE IS NOBODY TO GRANT THEM TO YET ════════════
         *
         * **Section 7, 2026-10-28.** Owner: *"בחרתי תפקיד וחברות בתוך האשף; אין סיבה שהבחירות
         * יאבדו ושאצטרך להגדיר אותן שוב אחר כך."*
         *
         * This branch is the whole defect he reported. An invited person has NO membership until
         * they redeem — `tenant.member.read` cannot return them — so `invited` was `undefined`,
         * both acts stayed `outstanding`, and everything the administrator had chosen existed only
         * in this component's state until the tab was closed.
         *
         * Two calls, each under the capability that governs the same act on a member who already
         * exists (`tenant.role.manage`, `companies.assignment.manage`). NOTHING IS GRANTED HERE:
         * the row is an instruction with no reader in the authorization path, and the redemption
         * is what spends it — re-checking the role, the company and the recorder's own active
         * membership, and recording the outcome of each. The separation OA-0001 §3.7 requires is
         * untouched: three calls, three capabilities, and a session holding only
         * `tenant.user.invite` records nothing and is told so.
         */
        if (roleCodes.length > 0 && mayAssignRole) {
          try {
            await callOperation('tenant.invitation.role.record', {
              body: { invitationUid: created.invitationUid, roleCodes },
            });
            roleResult = 'recorded';
          } catch {
            roleResult = 'refused';
          }
        }
        if (companyUids.length > 0 && mayGrantAccess) {
          try {
            await callOperation('tenant.invitation.access.record', {
              body: { invitationUid: created.invitationUid, companyUids, accessLevel: level },
            });
            grantResult = 'recorded';
          } catch {
            grantResult = 'refused';
          }
        }
      }

      /*
       * AND THEN WE ASK THE SERVER WHAT IT KEPT. A read, so it is safe to retry and safe to fail:
       * a summary without the read-back is still honest about the two acts above, and an empty
       * `pending` is printed as *"the server reports nothing waiting"* rather than as a success.
       */
      let pending: readonly PendingIntent[] = [];
      try {
        const answer = await callOperation('tenant.invitation.intent.read', {
          params: { invitation_uid: created.invitationUid },
        });
        /*
         * SHAPE-CHECKED, not trusted. This is the ONE read whose failure must not take the summary
         * with it: the two acts above have already happened and their verdicts are worth printing
         * whatever this answers. A body without `pending` would otherwise throw while rendering a
         * list, and the administrator would see a blank screen after a successful invitation.
         */
        pending = Array.isArray(answer.pending) ? answer.pending : [];
      } catch {
        pending = [];
      }

      setOutcome({
        kind: 'invited',
        invitationUid: created.invitationUid,
        delivery: isDeliveryReason(created.delivery) ? created.delivery : null,
        role: roleResult,
        grant: grantResult,
        pending,
      });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  // ── THE RESULT REPLACES THE FORM. Leaving the form up beside a result invites a second send.
  if (outcome !== null && outcome.kind === 'invited') {
    const delivery = deliveryTextFor(outcome.delivery);
    return (
      <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
        titleHe={t('invite.title')}
        backToParent={{ labelHe: t('users.title'), href: routes.tenantUsers }}
      >
        <StateBlock
          titleHe={delivery.titleHe}
          bodyHe={`${delivery.explanationHe} ${t('invite.selfPassword')}`}
          tone={delivery.tone === 'ok' ? 'empty' : delivery.tone === 'warn' ? 'unavailable' : 'error'}
          nextStep={
            <>
              <p>{delivery.nextStepHe}</p>
              {/*
                EACH ACT, NAMED AND WITH ITS OWN VERDICT. The Owner asked for *"the state of the
                invitation and what is needed to complete it"*, and three writes behind one word is
                exactly how somebody concludes a person has access when they have an account.
              */}
              <ul className="act-list">
                <li>{`${t('invite.stepRole')} — ${actHe(t, outcome.role)}`}</li>
                <li>{`${t('access.title')} — ${actHe(t, outcome.grant)}`}</li>
                <li>{t('invite.actInvited')}</li>
              </ul>
              {/*
                  WHAT THE SERVER SAYS IS WAITING — section 7, and the reason this is a separate
                  list. The one above reports what each ACT did; this one reports what the office's
                  database is actually holding, by NAME, read back from it. They are different
                  claims, and the second is the one the Owner asked to see.
              */}
              {outcome.role === 'recorded' || outcome.grant === 'recorded' ? (
                <>
                  <p className="act-list__title">{t('invite.pendingTitle')}</p>
                  {outcome.pending.length === 0 ? (
                    <p>{t('invite.pendingNone')}</p>
                  ) : (
                    <ul className="act-list">
                      {outcome.pending.map((row) => (
                        <li key={`${row.kind}:${row.roleCode ?? ''}:${row.companyName ?? ''}`}>
                          {row.kind === 'role'
                            ? `${t('invite.stepRole')} — ${roleNameFor(row.roleCode)}`
                            : `${row.companyName ?? ''} — ${accessLevelHe(
                                (row.accessLevel ?? 'read') as AccessLevel,
                              )}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : null}
              {/*
                  THE LINKS ARE FOR WORK THAT IS STILL OWED, and a RECORDED choice owes none.
                  **Section 7, 2026-10-28.** They appeared whenever either act was not `done`,
                  which after this round would include every normal invitation — sending an
                  administrator to the roles screen to redo by hand what the server is already
                  holding. They now appear only for an act that was REFUSED or left OUTSTANDING,
                  which are the two states that genuinely need somebody to go and finish them.
              */}
              {[outcome.role, outcome.grant].some(
                (act) => act === 'refused' || act === 'outstanding',
              ) ? (
                <div className="actions-row">
                  <a className="btn btn--nav" href={routes.tenantRoles}>
                    {t('roles.grantAction')}
                  </a>
                  <a className="btn" href={routes.tenantCompanyAccess}>
                    {t('access.grantTitle')}
                  </a>
                </div>
              ) : null}
            </>
          }
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('invite.title')}
      backToParent={{ labelHe: t('users.title'), href: routes.tenantUsers }}
    >
      {/* WHERE YOU ARE IN THE PROCESS. Positions, not fixed numbers: a session that cannot grant
          access sees three steps and is not told it is missing a fourth. */}
      <ol className="wizard-steps" aria-label={t('invite.stepsProgress')}>
        {STEPS.map((s, i) => (
          <li
            key={s.id}
            className="wizard-steps__step"
            aria-current={i + 1 === step ? 'step' : undefined}
            data-state={i + 1 === step ? 'current' : i + 1 < step ? 'done' : 'ahead'}
          >
            {/*
                THE NUMBER COMES FROM THE LIST, NOT FROM HERE. **Owner section 4, 2026-10-27:**
                *"להסיר מספור כפול כגון 1. 1. ו-2. 2."* -- this text carried its own `1. ` while the
                <ol> around it was already rendering a marker, so every step was numbered twice.
                Removing the text rather than the marker keeps the comment above true: the marker
                counts POSITIONS, so a session offered three steps is numbered 1-2-3 and is never
                told it is missing a fourth.
            */}
            {/*
                AND A STEP THAT IS BEHIND YOU IS A STEP YOU CAN GO BACK TO. **Section 4:**
                *"לאפשר חזרה לשלבים קודמים ללא אובדן נתונים."* Every answer lives in this
                component's state and nothing is sent before the summary, so moving between steps
                costs nothing -- the only thing missing was a way to do it in one click instead of
                pressing חזרה repeatedly.

                ONLY BACKWARDS. A step ahead has not been answered yet, and jumping forward past a
                required field is how somebody reaches the summary with an empty name.
            */}
            {i + 1 < step ? (
              <button type="button" className="wizard-steps__jump" onClick={() => setStep(i + 1)}>
                {s.labelHe}
              </button>
            ) : (
              <span className="wizard-steps__name">{s.labelHe}</span>
            )}
            {/*
                THE STATE, IN WORDS. Not a colour and not a tick alone: *"להציג שלבים ברורים עם
                השלב הנוכחי והשלבים שהושלמו"*, and a reader who cannot see the colour gets the
                same answer as one who can.
            */}
            <span className="wizard-steps__state">
              {i + 1 === step
                ? t('invite.stepStateCurrent')
                : i + 1 < step
                  ? t('invite.stepStateDone')
                  : t('invite.stepStateAhead')}
            </span>
          </li>
        ))}
      </ol>

      {/*
        THE STEPS THIS SESSION MAY NOT PERFORM, named with their reason. **Section 4, 2026-10-27.**
        They are not missing and they are not unfilled: they are closed, by the gate, and this is
        the one sentence that tells those two apart.
      */}
      {unavailableSteps.length === 0 ? null : (
        <p className="wizard-steps__unavailable">
          {`${unavailableSteps.join(' · ')} — ${t('invite.stepStateUnavailable')}. ${t('invite.stepsUnavailable')}`}
        </p>
      )}

      {/*
        SUBMIT ADVANCES UNTIL THE SUMMARY, AND ONLY THERE DOES IT SEND. Enter on a field therefore
        moves to the next step rather than inviting somebody halfway through a decision.
      */}
      <form
        className="form"
        onSubmit={(e) => {
          if (current?.id === 'summary') {
            void confirm(e);
            return;
          }
          e.preventDefault();
          /*
           * THE GATE IS ON THE FORM, NOT ONLY ON THE BUTTON. **Section 4/5, 2026-10-27.**
           *
           * A disabled button stops a click and nothing else: Enter in a field submits the form,
           * and the first version of this check lived on the button alone -- so the step the Owner
           * asked to make explicit could still be walked past with the keyboard. Found by a test
           * that advances by submitting the form, which is what a person pressing Enter does.
           */
          if (blockedHe !== null) return;
          setStep((n) => n + 1);
        }}
      >
        {current?.id === 'details' ? (
          <>
            {/*
              `required` WAS ALREADY ON BOTH INPUTS AND NOTHING SAID SO. **Section 4:** *"להבהיר
              שדות חובה בשלב שבו ממלאים אותם."* The attribute speaks to the browser; the mark
              beside the label speaks to the person filling the form, and the sentence under the
              button says what is still missing.
            */}
            <label className="field">
              <span className="field__label">
                {t('invite.name')}
                <span className="field__required">{t('invite.required')}</span>
              </span>
              <input
                className="field__input"
                name="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">
                {t('invite.email')}
                <span className="field__required">{t('invite.required')}</span>
              </span>
              <input
                className="field__input"
                name="email"
                type="email"
                dir="ltr"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
              />
            </label>
          </>
        ) : current?.id === 'role' ? (
          <ServerRead result={roles.result} whatHe={t('roles.reading')}>
            {(data) => (
              <fieldset className="field field--choice">
                <legend className="field__label">{t('invite.stepRole')}</legend>
                {data.roles.map((role) => (
                  <label key={role.roleCode} className="choice">
                    <input
                      type="checkbox"
                      name="roleCodes"
                      value={role.roleCode}
                      checked={roleCodes.includes(role.roleCode)}
                      onChange={(e) => {
                        // READ IT HERE. `currentTarget` is null by the time an updater runs.
                        const ticked = e.currentTarget.checked;
                        setRoleCodes((was) =>
                          ticked ? [...was, role.roleCode] : was.filter((c) => c !== role.roleCode),
                        );
                        if (ticked) setNoRole(false);
                      }}
                    />
                    <span>{roleLabel(role)}</span>
                  </label>
                ))}
                {/*
                  THE DECISION, MADE HERE RATHER THAN DISCOVERED IN THE SUMMARY. Ticking this
                  clears every chosen role, and choosing a role clears this: one answer, never two.
                */}
                <label className="choice choice--optout">
                  <input
                    type="checkbox"
                    name="noRole"
                    checked={noRole}
                    onChange={(e) => {
                      const ticked = e.currentTarget.checked;
                      setNoRole(ticked);
                      if (ticked) setRoleCodes([]);
                    }}
                  />
                  <span>{t('invite.noRoleOptIn')}</span>
                </label>
                <span className="field__hint">
                  {t('invite.roleHint')}
                </span>
              </fieldset>
            )}
          </ServerRead>
        ) : current?.id === 'access' ? (
          <ServerRead result={register.result} whatHe={t('companies.reading')}>
            {(data) => (
              <>
                <fieldset className="field field--choice">
                  <legend className="field__label">{t('settings.e.companies')}</legend>
                  {data.companies.map((company) => (
                    <label key={company.companyUid} className="choice">
                      <input
                        type="checkbox"
                        name="companyUids"
                        value={company.companyUid}
                        checked={companyUids.includes(company.companyUid)}
                        onChange={(e) => {
                          const ticked = e.currentTarget.checked;
                          setCompanyUids((was) =>
                            ticked
                              ? [...was, company.companyUid]
                              : was.filter((u) => u !== company.companyUid),
                          );
                          if (ticked) setNoAccess(false);
                        }}
                      />
                      <span>{company.displayName}</span>
                    </label>
                  ))}
                  <label className="choice choice--optout">
                    <input
                      type="checkbox"
                      name="noAccess"
                      checked={noAccess}
                      onChange={(e) => {
                        const ticked = e.currentTarget.checked;
                        setNoAccess(ticked);
                        if (ticked) setCompanyUids([]);
                      }}
                    />
                    <span>{t('invite.noAccessOptIn')}</span>
                  </label>
                </fieldset>

                {/* ONE NAMED FIELD, chosen from a list. See `access.level` in the catalogue. */}
                <label className="field">
                  <span className="field__label">{t('access.level')}</span>
                  <select
                    className="field__control"
                    name="accessLevel"
                    value={level}
                    onChange={(e) => setLevel(e.currentTarget.value as AccessLevel)}
                  >
                    {OFFERED_ACCESS_LEVELS.map((value) => (
                      <option key={value} value={value}>
                        {accessLevelHe(value)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
          </ServerRead>
        ) : (
          <section className="notice" data-tone="info">
            <h2 className="notice__title">{t('invite.aboutToDo')}</h2>
            {/*
              THE SUMMARY IS WRITTEN FROM THE CHOICES, so what it lists is what will be sent -- and
              it lists them BY NAME. **Section 4:** *"בסיכום להציג שמות תפקידים וחברות ורמת גישה,
              ולא רק מספרים."* It said `תפקיד: 2`, which is a number an administrator cannot check
              anything against; the names come from the same answers the ticks were drawn from.
            */}
            <ul className="act-list">
              <li>{`${t('invite.title')}: ${displayName} · ${email}`}</li>
              <li>
                {chosenRoleNames.length === 0
                  ? t('invite.noRoleConfirmed')
                  : `${t('invite.stepRole')}: ${chosenRoleNames.join(' · ')}`}
              </li>
              <li>
                {chosenCompanyNames.length === 0
                  ? t('invite.noAccessConfirmed')
                  : `${t('access.title')}: ${chosenCompanyNames.join(' · ')} — ${t('access.level')}: ${accessLevelHe(level)}`}
              </li>
              {/* The inviter's own question at this moment, answered before they press. */}
              <li>{t('invite.selfPassword')}</li>
            </ul>
            {mayAssignRole && mayGrantAccess ? null : (
              // WHAT THIS SESSION MAY NOT DO, said before the send and not after it.
              <p className="notice__body">
                {`${t('invite.stepsUnavailable')}${mayAssignRole ? '' : ` — ${t('invite.stepRole')}`}${mayGrantAccess ? '' : ` — ${t('access.grantTitle')}`}`}
              </p>
            )}
          </section>
        )}

        <div className="actions-row">
          {step > 1 ? (
            <button type="button" className="btn" onClick={() => setStep((n) => n - 1)}>
              {t('common.back')}
            </button>
          ) : null}
          {/*
            ONE PRIMARY BUTTON, whose label and effect follow the step.
            Written as two buttons in a ternary first, which `actionAffordance.test.ts` refused:
            it reads source and counts `btn--primary` inside a form, and two of them is the shape
            that puts a second save beside a save. They could never render together -- and the
            check is still right, because a reader of this file has to work that out, and the next
            person adding a button here would copy the shape rather than the reasoning.
          */}
          <button
            type="submit"
            className="btn btn--primary"
            disabled={pending || blockedHe !== null}
          >
            {pending
              ? t('common.working')
              : current?.id === 'summary'
                ? t('invite.confirm')
                : t('common.continue')}
          </button>
          <a className="btn" href={routes.tenantUsers}>
            {t('common.cancel')}
          </a>
        </div>

        {/*
          A DISABLED CONTROL THAT SAYS WHY. The button was disabled on an unfilled details step and
          explained nothing, and the role step had no condition at all -- which is how a person
          reached the summary without ever deciding about a role.
        */}
        {blockedHe === null ? null : <p className="field__hint field__hint--blocking">{blockedHe}</p>}
      </form>

      {outcome === null || outcome.kind !== 'failed' ? null : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
            THE CARD NAMES THE BUTTON THAT FINISHES THE JOB. **Section 6, 2026-10-28.** The write
            was refused before the factor was given, so after the factor there is still a press to
            make -- and the Owner, reasonably, read the silence as a failed first click.
          */
          continueLabelHe={t('invite.confirm')}
          /*
           * ── SECTION 3, 2026-10-27: THE FACTOR IS GIVEN HERE, NOT SOMEWHERE ELSE ──────────────
           *
           * Owner acceptance test: *"הגעתי לסיכום ולאישור, והשלמתי אימות נוסף. לאחר מכן חזרתי
           * לטופס ריק ללא הודעת תוצאה."*
           *
           * Without this handler, `ActionFailureNotice` has no way to satisfy an `MFA` demand in
           * place, so it falls through to the arm that renders a LINK TO ANOTHER ADDRESS. Every
           * answer in this wizard lives in component state; following that link unmounts the
           * screen, and coming back mounts a new one at step 1 with empty fields and no outcome.
           * The invitation was never created either, because the write was refused before it — so
           * the person is told nothing, has lost their work, and the list is unchanged.
           *
           * IT DOES NOT RE-SEND THE REFUSED ACTION, and that is `TenantUsersScreen`'s rule rather
           * than a shortcut here: *"A write that replays itself after an authentication step is a
           * write nobody pressed."* Clearing the outcome puts the person back on the summary with
           * every choice intact; they press confirm, and this time the gate finds a fresh
           * assertion.
           *
           * THE STEP IS NOT REWOUND. `setStep` is deliberately not called: the person was on the
           * summary when the server asked, and moving them is the thing this repair exists to stop.
           */
          onStepUpSatisfied={() => {
            setOutcome(null);
          }}
          renderConflict={(failure) => {
            /*
              08 §7 ON THIS SCREEN TOO. The seat limit is the refusal an invitation actually hits,
              and it arrives with the count, the limit and the address that resolves it -- the same
              component `TenantCompaniesScreen` uses for the company limit, so the two never drift.
            */
            /*
              THE DUPLICATE, ANSWERED AS WHAT IT IS. A second attempt for the same address is
              refused by the server, so no duplicate is created -- and this is not a failure the
              inviter caused. The invitation is already there; what they want is a resend, and the
              users list is where that lives.
            */
            if (failure.body.error === 'already_invited') {
              return (
                <StateBlock
                  titleHe={t('invite.alreadyTitle')}
                  bodyHe={t('invite.alreadyBody')}
                  tone="unavailable"
                  nextStep={
                    <a className="btn btn--nav" href={routes.tenantUsers}>
                      {t('invite.alreadyAction')}
                    </a>
                  }
                />
              );
            }
            if (
              failure.body.error !== 'limit_reached' ||
              failure.body.current === undefined ||
              failure.body.limit === undefined
            ) {
              return null;
            }
            return (
              <CapacityRefusalNotice
                refusal={{
                  reason: 'limit_reached',
                  kind: 'seat',
                  current: failure.body.current,
                  limit: failure.body.limit,
                  // An address inside this product. The server's `upgradePath` is not pasted into
                  // an `href` unexamined: `routes.ts` is the only place a URL is built (09 §1.5).
                  upgradeHref: routes.tenantSubscription,
                }}
              />
            );
          }}
        />
      )}
    </AppShell>
  );
}
